import { c as _c } from "react/compiler-runtime";
import { getOrgEntityStatusesOptions } from '@onefootprint/fetch-dashboard';
import { Card, Divider } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { AnimatePresence, easeInOut, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import CustomStatusSelectors from '@/components/custom-status-selectors';
import FraudLabel from '@/components/fraud-label';
import IdDropdown, { type IdItem } from '@/components/id-dropdown';
import Labels from '@/components/labels';
import StatusBadge from '@/components/status-badge';
import Tags from '@/components/tags';
import type { WithEntityProps } from '@/entity/components/with-entity';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import { useEntityContext } from '@/entity/hooks/use-entity-context';
import useOnboardings from '../../../../hooks/use-onboardings';
import VaultActions from '../vault-actions';
import { LatestOnboarding } from './components/latest-onboarding/latest-onboarding';
type HeaderProps = WithEntityProps & {
  // Forwarded to VaultActions: hides the Decrypt SplitButton when false. Used by
  // hosts that mount this header outside the /users/[id] route.
  showDecrypt?: boolean;
};
const Header = t0 => {
  const $ = _c(71);
  const {
    entity,
    showDecrypt: t1
  } = t0;
  const showDecrypt = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "header"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t2);
  const {
    kind
  } = useEntityContext();
  const onboardings = useOnboardings(entity.id);
  const onboarding = onboardings[0];
  let t3;
  if ($[1] !== entity.kind) {
    t3 = getOrgEntityStatusesOptions({
      query: {
        entityType: entity.kind
      }
    });
    $[1] = entity.kind;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    data: customStatuses
  } = useQuery(t3);
  const {
    step: decryptStep
  } = useDecryptControls();
  let t4;
  if ($[3] !== t) {
    t4 = t(_temp);
    $[3] = t;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== entity.id || $[6] !== t4) {
    t5 = {
      label: t4,
      value: entity.id
    };
    $[5] = entity.id;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== entity.sandboxId || $[9] !== t) {
    t6 = entity.sandboxId ? {
      label: t(_temp2),
      value: entity.sandboxId
    } : null;
    $[8] = entity.sandboxId;
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== entity.externalId || $[12] !== t) {
    t7 = entity.externalId ? {
      label: t(_temp3),
      value: entity.externalId
    } : null;
    $[11] = entity.externalId;
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== entity.svId || $[15] !== t) {
    t8 = entity.svId ? {
      label: t(_temp4),
      value: entity.svId
    } : null;
    $[14] = entity.svId;
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== entity.vId || $[18] !== t) {
    t9 = entity.vId ? {
      label: t(_temp5),
      value: entity.vId
    } : null;
    $[17] = entity.vId;
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t10 = [t5, t6, t7, t8, t9].filter(_temp6);
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const idItems = t10;
  const t11 = decryptStep === "idle" ? "relative" : "sticky top-0 right-0 left-0";
  let t12;
  if ($[26] !== t11) {
    t12 = cx("z-sticky md:sticky md:top-0 md:right-0 md:left-0", t11);
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== kind || $[29] !== t) {
    t13 = kind === "person" ? t(_temp7) : t(_temp8);
    $[28] = kind;
    $[29] = t;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== idItems) {
    t14 = <IdDropdown items={idItems} />;
    $[31] = idItems;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span className="hidden text-heading-5 lg:block">⋅</span>;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== entity.requiresManualReview || $[35] !== entity.status) {
    t16 = <StatusBadge status={entity.status} requiresManualReview={entity.requiresManualReview} />;
    $[34] = entity.requiresManualReview;
    $[35] = entity.status;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== entity) {
    t17 = <Labels entity={entity} />;
    $[37] = entity;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <FraudLabel />;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t16 || $[41] !== t17) {
    t19 = <div className="flex flex-wrap gap-2 items-center md:flex-nowrap">{t16}{t17}{t18}</div>;
    $[40] = t16;
    $[41] = t17;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t14 || $[44] !== t19) {
    t20 = <div className="flex flex-wrap gap-2 items-start">{t14}{t15}{t19}</div>;
    $[43] = t14;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== entity || $[47] !== showDecrypt) {
    t21 = <VaultActions entity={entity} showDecrypt={showDecrypt} />;
    $[46] = entity;
    $[47] = showDecrypt;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t20 || $[50] !== t21) {
    t22 = <div className="flex flex-wrap gap-2 justify-between items-center w-full md:flex-nowrap">{t20}{t21}</div>;
    $[49] = t20;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Tags />;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== customStatuses || $[54] !== entity.id || $[55] !== entity.statuses) {
    t24 = customStatuses && customStatuses.length > 0 && <motion.div className="flex flex-col gap-3 mt-1 w-full" exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.1,
      ease: easeInOut
    }}><Divider /><CustomStatusSelectors customStatuses={customStatuses} assignedStatuses={entity.statuses} entityId={entity.id} /></motion.div>;
    $[53] = customStatuses;
    $[54] = entity.id;
    $[55] = entity.statuses;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t24) {
    t25 = <AnimatePresence mode="wait">{t24}</AnimatePresence>;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== t22 || $[60] !== t25) {
    t26 = <Card.Body className="flex flex-col gap-2 justify-center items-start p-3">{t22}{t23}{t25}</Card.Body>;
    $[59] = t22;
    $[60] = t25;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== onboarding) {
    t27 = onboarding && <LatestOnboarding onboarding={onboarding} />;
    $[62] = onboarding;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t26 || $[65] !== t27) {
    t28 = <Card.Root>{t26}{t27}</Card.Root>;
    $[64] = t26;
    $[65] = t27;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== t12 || $[68] !== t13 || $[69] !== t28) {
    t29 = <header className={t12} aria-label={t13}>{t28}</header>;
    $[67] = t12;
    $[68] = t13;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  return t29;
};
export default Header;
function _temp($) {
  return $.idDropdown.footprintId;
}
function _temp2($_0) {
  return $_0.idDropdown.sandboxId;
}
function _temp3($_1) {
  return $_1.idDropdown.externalId;
}
function _temp4($_2) {
  return $_2.idDropdown.scopedVaultId;
}
function _temp5($_3) {
  return $_3.idDropdown.vaultId;
}
function _temp6(item) {
  return item !== null;
}
function _temp7($_4) {
  return $_4.ariaLabel.person;
}
function _temp8($_5) {
  return $_5.ariaLabel.business;
}